import { formatDateWithDynamicTimezone } from "@marketplace/shared/utils";
import { Text } from "@marketplace/ui";
import { notifications } from "@marketplace/ui/notifications";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { DeletePackageModal } from "../../components/delete-package-modal";
import { DeleteVariantModal } from "../../components/delete-variant-modal";
import { EditProductForm } from "../../components/edit-product-form";
import { PreviewButton } from "../../components/preview-button";
import { ProductActions } from "../../components/product-actions";
import { ProductGuide } from "../../components/product-guide";
import { useEditProductForm } from "../../hooks/forms/use-edit-product-form";
import { useActiveSection } from "../../hooks/use-active-section";

const route = getRouteApi("/products/list/$slug/edit");

export const EditProductPage = () => {
  const { t: tProduct } = useTranslation("product");
  const { draft } = route.useSearch();
  const navigate = useNavigate();
  const {
    formMethods,
    productCategory,
    onSubmit,
    isPending,
    onSaveDraft,
    isSavingDraft,
    isLoading,
    isNotFound,
    updatedAt
  } = useEditProductForm();

  // Define section IDs based on product category
  const sectionIds = useMemo(() => {
    const baseSections = [
      "product-detail",
      "media",
      "recurring-order",
      "product-packages"
    ];

    if (productCategory === "api") {
      baseSections.push("api-package");
    }

    baseSections.push("terms-condition");
    return baseSections;
  }, [productCategory]);

  const activeSection = useActiveSection(sectionIds);

  useEffect(() => {
    if (isNotFound) {
      notifications.show({
        variant: "error",
        message: tProduct("editProductPage.notifications.productNotFound")
      });
      navigate({ to: "/products/list", replace: true });
    }
  }, [isNotFound, navigate, tProduct]);

  if (isLoading || isNotFound) {
    return null;
  }

  return (
    <FormProvider {...formMethods}>
      <DeletePackageModal />
      <DeleteVariantModal />
      <div className="flex flex-col gap-y-6">
        {/* Page Header */}
        <div className="flex justify-between items-center">
          <div className="space-y-1">
            <Text
              component="h1"
              type="heading"
              size="xl"
              weight="bold"
              className="text-fjord-grey-500"
            >
              {tProduct("editProductPage.title")}
            </Text>
            <div className="flex items-center gap-x-2">
              <Text type="paragraph" className="text-fjord-grey-400">
                {tProduct("editProductPage.description")}
              </Text>
              {updatedAt && (
                <>
                  <div className="w-[1px] h-[14px] bg-mischka-400" />
                  <Text className="text-fjord-grey-400">
                    {tProduct("editProductPage.lastModified")}{" "}
                    {formatDateWithDynamicTimezone(updatedAt)}
                  </Text>
                </>
              )}
            </div>
          </div>
          <PreviewButton productCategory={productCategory} />
        </div>

        {/* 3-Column Layout: Guide | Form | Actions */}
        <div className="flex gap-x-6">
          {/* Left Sticky Guide */}
          <ProductGuide
            productCategory={productCategory ?? "software"}
            activeSection={activeSection}
          />

          {/* Center Form */}
          <div className="flex-1">
            <form onSubmit={onSubmit}>
              <EditProductForm
                formMethods={formMethods}
                productCategory={productCategory ?? "software"}
              />
            </form>
          </div>

          {/* Right Sticky Actions */}
          <ProductActions
            onPublish={onSubmit}
            onSaveDraft={onSaveDraft}
            isPending={isPending}
            isSavingDraft={isSavingDraft}
            isEditingPublishedProduct={!draft}
          />
        </div>
      </div>
    </FormProvider>
  );
};
